const readPositiveInt = (value: string | null | undefined): number | null => {
  if (value == null || !/^\d+$/.test(String(value))) return null;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export const COMPANY_CONTEXT_CHANGED_EVENT = "aviraapro:company-context-changed";

export type AuthorizedCompanySelection = { id: number };
export type CompanySwitchResult = "switched" | "same_company" | "locked" | "unauthorized";

export const switchAuthorizedCompany = <T extends AuthorizedCompanySelection>({
  authorizedCompanies,
  currentCompanyId,
  targetCompanyId,
  companyContextLocked,
  syncCompany,
  reload,
}: {
  authorizedCompanies: T[];
  currentCompanyId: number;
  targetCompanyId: number;
  companyContextLocked: boolean;
  syncCompany: (company: T) => void;
  reload: () => void;
}): CompanySwitchResult => {
  if (companyContextLocked) return "locked";
  if (!Number.isInteger(targetCompanyId) || targetCompanyId <= 0) return "unauthorized";
  const target = authorizedCompanies.find((company) => company.id === targetCompanyId);
  if (!target) return "unauthorized";
  if (currentCompanyId === targetCompanyId) return "same_company";
  syncCompany(target);
  // One full reload clears module-scope CID captures and every mounted page's
  // old-company state. The caller must not dispatch another reload event.
  reload();
  return "switched";
};

const apiPathname = (url: string): string => {
  try {
    return new URL(url, "http://aviraapro.local").pathname.replace(/\/+$/, "") || "/";
  } catch {
    return String(url || "").split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/";
  }
};

/**
 * Routes in this list deliberately manage or compare more than one company.
 * Their company_id values are business targets, not the caller's selected
 * company, so the shared API client and middleware must never rewrite them.
 * Keep this matcher narrow: ordinary ERP routes remain selected-company scoped.
 */
export const hasExplicitCrossCompanyQueryTargets = (url: string): boolean => {
  const path = apiPathname(url);
  if (path === "/api/mc" || path.startsWith("/api/mc/")) return true;
  if (
    path === "/api/platform-control"
    || (
      path.startsWith("/api/platform-control/")
      && path !== "/api/platform-control/me/modules"
    )
  ) return true;
  if (
    (path === "/api/tenants" || path.startsWith("/api/tenants/"))
    && path !== "/api/tenants/me/context"
  ) return true;
  return false;
};

export const hasExplicitCrossCompanyBodyTargets = (url: string): boolean => {
  const path = apiPathname(url);
  return hasExplicitCrossCompanyQueryTargets(url)
    || /^\/api\/users-v2\/[1-9]\d*\/company-access$/.test(path);
};

export const isExplicitCrossCompanyApiUrl = (url: string): boolean =>
  hasExplicitCrossCompanyBodyTargets(url);

const decodeJwtPayload = (): any | null => {
  try {
    const token = localStorage.getItem("token") || localStorage.getItem("erp_token");
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
    return JSON.parse(atob(b64 + pad));
  } catch {
    return null;
  }
};

const getPositiveContextId = (keys: string[], tokenClaim: string): number | null => {
  try {
    for (const key of keys) {
      const id = readPositiveInt(localStorage.getItem(key));
      if (id) return id;
    }
    const claim = decodeJwtPayload()?.[tokenClaim];
    return readPositiveInt(claim == null ? null : String(claim));
  } catch {
    return null;
  }
};

export const getOptionalSelectedCompanyId = (): number | null =>
  getPositiveContextId(["company_id", "erp_company_id", "activeCompanyId"], "company_id");

export const getOptionalSelectedTenantId = (): number | null =>
  getPositiveContextId(["tenant_id", "erp_tenant_id"], "tenant_id");

export const getSelectedCompanyId = (): number => getOptionalSelectedCompanyId() ?? 0;

export const getSelectedTenantId = (): number => getOptionalSelectedTenantId() ?? 0;

export const getSelectedBaseCurrency = (): string => {
  try {
    const stored = String(
      localStorage.getItem("erp_company_base_currency")
      || localStorage.getItem("base_currency")
      || "",
    ).trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(stored)) return stored;
    const tokenCurrency = String(decodeJwtPayload()?.base_currency || "").trim().toUpperCase();
    return /^[A-Z]{3}$/.test(tokenCurrency) ? tokenCurrency : "";
  } catch {
    return "";
  }
};

export const getSelectedDisplayLocale = (): string | undefined => {
  try {
    const configured = String(localStorage.getItem("erp_company_display_locale") || "").trim();
    if (!configured) return undefined;
    // Let the runtime validate the configured BCP-47 tag. Undefined is the
    // neutral runtime locale; there is no country-specific code fallback.
    new Intl.NumberFormat(configured);
    return configured;
  } catch {
    return undefined;
  }
};

export const getSelectedTimezone = (): string | undefined => {
  try {
    const configured = String(localStorage.getItem("erp_company_timezone") || "").trim();
    if (!configured) return undefined;
    new Intl.DateTimeFormat(undefined, { timeZone: configured });
    return configured;
  } catch {
    return undefined;
  }
};

export type CompanyNumberingSystem = "INTERNATIONAL" | "INDIAN" | "DIGITS";

export const getSelectedNumberingSystem = (): CompanyNumberingSystem | undefined => {
  try {
    const configured = String(
      localStorage.getItem("erp_company_numbering_system") || "",
    ).trim().toUpperCase();
    return ["INTERNATIONAL", "INDIAN", "DIGITS"].includes(configured)
      ? configured as CompanyNumberingSystem
      : undefined;
  } catch {
    return undefined;
  }
};

export const formatSelectedCompanyNumber = (
  value: unknown,
  options: Intl.NumberFormatOptions = {},
): string => {
  const numericValue = Number(value ?? 0);
  const amount = Number.isFinite(numericValue) ? numericValue : 0;
  return amount.toLocaleString(getSelectedDisplayLocale(), options);
};

export const formatSelectedCompanyDateTime = (
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions = {},
): string => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  const timezone = getSelectedTimezone();
  return new Intl.DateTimeFormat(getSelectedDisplayLocale(), {
    ...options,
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(parsed);
};

export const formatSelectedCompanyMoney = (
  value: unknown,
  options: Intl.NumberFormatOptions = {},
): string => {
  const numericValue = Number(value ?? 0);
  const amount = Number.isFinite(numericValue) ? numericValue : 0;
  const currency = getSelectedBaseCurrency();
  const numberOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  };

  if (!currency) {
    // Fail closed: show the numeric amount without claiming a currency when
    // the selected company's signed/configured base currency is unavailable.
    return formatSelectedCompanyNumber(amount, numberOptions);
  }

  try {
    return new Intl.NumberFormat(getSelectedDisplayLocale(), {
      ...numberOptions,
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    }).format(amount);
  } catch {
    // The currency code has already passed the ISO-shaped validation above;
    // retain an explicit code if this runtime does not support that currency.
    return `${currency} ${formatSelectedCompanyNumber(amount, numberOptions)}`;
  }
};
